import React, { useEffect, createContext, useCallback, useState, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getAssessmentDefFetch, getAssessmentDefsFetch, getAssessmentDefTypesFetch, getAssessmentFetch, getCategoriesFetch, getPublishersFetch, getReadabilityScoresFetch } from 'store/middlewares/assessment.crud'
import { queryClient as qc } from 'keys'
import { Form } from 'components/Form'
import { isEqual, isNumber, uniqueId } from 'lodash'
import { categories as categorieKeys } from 'keys'
import { change, initialize, isValid, reset, submit } from 'redux-form'
import { toast } from 'react-toastify'
import { toastConfig } from 'config'
import { useDispatch, useSelector } from 'react-redux'
import { Title as ScreenTitle } from 'components'
import { getAffiliationsFetch } from 'store/middlewares/affiliation.crud'
import { useNavigate, useParams } from 'react-router-dom'
import { CreateAssessmentDefStyles } from './Style'

import validate from './validate'
import StepsFooter from './StepsFooter'
import MultipleChoise from './MultipleChoise'
import Book from './Book'
import Words from './Words'
import CreateAssessmentDef from './CreateAssessmentDef'
import { AssessmentDefDrawer } from 'pages/School/AssessmentDef'
import { app } from 'router/routes'

const CreateAssessmentDefPage = (props) => {
    const [createAssessmentStep, setCreateAssessmentStep] = useState(0)
    const [currentItemIndex, setCurrentItemIndex] = useState(0)
    const [grade, setGrade] = useState(null)
    const [textUpdated, setTextUpdated] = useState(null)

    const isFormValid = useSelector(state => isValid('createAssessmentDef')(state));
    const formValues = useSelector(state => state.form.createAssessmentDef?.values)
    const dispatch = useDispatch()
    const queryClient = useQueryClient()
    const params = useParams()
    const navigate = useNavigate()
    const prevText = useRef(formValues?.pages)
    const { assessmentDefID } = params

    const categories = queryClient.getQueryData([qc.categories])

    const assessmentDef = useQuery({
        queryKey: [qc.assessmentDef, assessmentDefID],
        queryFn: async () => {
            const { data } = await getAssessmentDefFetch({ id: assessmentDefID })
            return data
        },
        enabled: !!assessmentDefID
    })

    const assessmentDefs = useQuery({
        queryKey: [qc.assessmentDefs],
        queryFn: async () => {
            const { data } = await getAssessmentDefsFetch({
                assessmentFilters: {
                    category:
                        categories.filter((category) =>
                            // רשימת הקטגוריות הפתוחות
                            category.name == categorieKeys.NARRATIVE_TEXT ||
                            category.name == categorieKeys.NARRATIVE_TEXT_NO_NIKUD ||
                            category.name == categorieKeys.INFORMATIONAL_TEXT ||
                            category.name == categorieKeys.INFORMATIONAL_TEXT_NO_NIKUD)
                }
            })
            return data
        },
        enabled: !!categories
    })

    const assessmentDefTypes = useQuery({
        queryKey: [qc.assessmentDefTypes],
        queryFn: async () => {
            const { data } = await getAssessmentDefTypesFetch()
            return data
        }
    })

    const sourceAssessmentDef = useQuery({
        queryKey: [qc.assessmentDef, "assessment-def-view", formValues?.source_passage],
        queryFn: async () => {
            const { data } = await getAssessmentDefFetch({ id: formValues.source_passage.id })
            return data
        },
        enabled: !!formValues?.source_passage?.id
    })

    const publishers = useQuery({
        queryKey: [qc.publishers],
        queryFn: async () => {
            const { data } = await getPublishersFetch()
            return data
        }
    })

    const affiliations = useQuery({
        queryKey: [qc.affiliations],
        queryFn: async () => {
            const { data } = await getAffiliationsFetch()
            return data
        }
    })

    const readabilityScores = useQuery({
        queryKey: [qc.readabilityScores],
        queryFn: async () => {
            const { data } = await getReadabilityScoresFetch()
            return data
        }
    })

    let categoryName = formValues?.category?.name || categories?.find(category => category.id == formValues?.category)?.name

    const question = React.useMemo(() => ({
        id: uniqueId(),
        text: "",
        answer_defs: Array.from({ length: 4 }, () => ({
            id: uniqueId(),
            text: "",
            is_correct: false
        }))
    }), [])

    const goNextStep = useCallback((nextStep) => {
        if (!isFormValid) {
            toast.error('מלא את כל שדות החובה', toastConfig)
            dispatch(submit('createAssessmentDef')) // Trigger validation for the current step
        }
        else if (!categoryViews[categoryName]) {
            toast.error("קטגוריה זו עדיין נעולה ליצירת מבדקים", toastConfig)

            return false
        }
        else {
            if (isNumber(nextStep))
                setCreateAssessmentStep(nextStep)
            else setCreateAssessmentStep(currentStep => currentStep + 1)
        }
    }, [formValues, isFormValid, categoryName])

    const goAssessmentLibrary = useCallback((step) => {
        navigate(app.assessmentLibrary)
    }, [])

    const categoryViews = {
        [categorieKeys.MULTIPLE_CHOICE_QUESTIONS]: {
            itemsKey: "questions",
            name: categorieKeys.MULTIPLE_CHOICE_QUESTIONS,
            emptyItem: question,
            Component: MultipleChoise,
            label: "שאלות המבדק"
        },
        [categorieKeys.NARRATIVE_TEXT]: {
            Component: Book,
            name: categorieKeys.NARRATIVE_TEXT,
            itemsKey: "pages",
            emptyItem: ["", ""],
        },
        [categorieKeys.NARRATIVE_TEXT_NO_NIKUD]: {
            Component: Book,
            name: categorieKeys.NARRATIVE_TEXT_NO_NIKUD,
            itemsKey: "pages",
            emptyItem: ["", ""],
        },
        [categorieKeys.INFORMATIONAL_TEXT]: {
            Component: Book,
            name: categorieKeys.INFORMATIONAL_TEXT,
            itemsKey: "pages",
            emptyItem: ["", ""],
        },
        [categorieKeys.INFORMATIONAL_TEXT_NO_NIKUD]: {
            Component: Book,
            name: categorieKeys.INFORMATIONAL_TEXT_NO_NIKUD,
            itemsKey: "pages",
            emptyItem: ["", ""],
        },
        [categorieKeys.REAL_WORDS]: {
            Component: Words,
            name: categorieKeys.REAL_WORDS,
            itemsKey: "words",
            emptyItem: new Array(9).fill(""),
            cardLabel: "מילה"
        },
        [categorieKeys.REAL_WORDS_NO_NIKUD]: {
            Component: Words,
            name: categorieKeys.REAL_WORDS_NO_NIKUD,
            itemsKey: "words",
            emptyItem: new Array(9).fill(""),
            cardLabel: "מילה"
        },
        [categorieKeys.NONSENSE_WORDS]: {
            Component: Words,
            name: categorieKeys.NONSENSE_WORDS,
            itemsKey: "words",
            emptyItem: new Array(9).fill(""),
            cardLabel: "מילה"
        },
        [categorieKeys.LETTER_NAMING]: {
            Component: Words,
            name: categorieKeys.LETTER_NAMING,
            itemsKey: "words",
            cardLabel: "אות",
            emptyItem: new Array(9).fill(""),
        },
        [categorieKeys.LETTER_SOUNDS]: {
            Component: Words,
            name: categorieKeys.LETTER_SOUNDS,
            itemsKey: "words",
            cardLabel: "אות",
            emptyItem: new Array(9).fill(""),
        },
        [categorieKeys.LETTER_VOWEL_CLUSTERS]: {
            Component: Words,
            name: categorieKeys.LETTER_VOWEL_CLUSTERS,
            itemsKey: "words",
            cardLabel: "אות",
            emptyItem: new Array(9).fill(""),
        },
        [categorieKeys.VOWEL_SOUND]: {
            Component: Words,
            name: categorieKeys.VOWEL_SOUND,
            itemsKey: "words",
            cardLabel: "אות",
            emptyItem: new Array(9).fill(""),
        },
        "default": {
            Component: Book,
            emptyItem: ["", ""],
        }
    }

    let categoryItem = categoryViews[categoryName]

    const assessmentSteps = [
        { Component: CreateAssessmentDef },
        { Component: categoryItem?.Component }
    ]

    const CurrentComponent = assessmentSteps[createAssessmentStep].Component

    const commonValue = {
        assessmentDefTypes,
        readabilityScores,
        assessmentDefs,
        categoryItem,
        createAssessmentStep, setCreateAssessmentStep,
        question,
        textUpdated, setTextUpdated,
        categoryViews,
        assessmentDef,
        categories,
        publishers,
        affiliations,
        grade, setGrade,
        currentItemIndex, setCurrentItemIndex,
        goNextStep
    }

    useEffect(() => {
        return (() => {
            dispatch(reset("createAssessmentDef"))
            dispatch(initialize('createAssessmentDef', {}));
        })
    }, [])

    const { title, text } = sourceAssessmentDef?.data || {}

    return (
        <CreateAssessmentDefContext.Provider value={commonValue}>
            <CreateAssessmentDefStyles>
                <ScreenTitle  {...props} breadcrumbs={true}>
                    <div className='bread-crumbs-title'>
                        {assessmentDefID ?
                            <React.Fragment>
                                {" > "}
                                {"עריכת מבדק "}
                                {` - ${assessmentDef.data?.name || ""}`}
                            </React.Fragment>
                            :
                            <React.Fragment>
                                {" > "}
                                {"הוספת מבדק חדש"}
                            </React.Fragment>
                        }
                    </div>
                </ScreenTitle>
                <div className='assessment-def-content'>
                    <Form
                        form={"createAssessmentDef"}
                        title={null}
                        validations={validate}
                        submitBtns={[]}
                        submit={() => { }}
                        initialValues={assessmentDef.data}
                    >
                        <CurrentComponent />
                    </Form>
                    {!formValues?.source_passage ||
                        <div className='question-section view-assessment-def'>
                            <div className='title'>
                                {title}
                            </div>
                            <div dangerouslySetInnerHTML={{ __html: text?.replace(/\r?\n/g, "<br />") }} />
                        </div>}
                </div>
                <StepsFooter />
            </CreateAssessmentDefStyles>
        </CreateAssessmentDefContext.Provider>
    )
}
export const CreateAssessmentDefContext = createContext()
export default CreateAssessmentDefPage